import { useEffect, useState } from "react";
import { useLang } from "../i18n";
import { Menu, X } from "lucide-react";

export function HelmetIcon({ className = "w-5 h-5", visor = "#FFCC00" }) {
  return (
    <svg viewBox="0 0 32 32" className={className} fill="none" aria-hidden="true">
      <path
        d="M16 3C8.8 3 3 8.9 3 16.1c0 2.6.8 5 2.1 7 .5.8 1.4 1.3 2.3 1.3h15.2c1.9 0 3.4-1.5 3.4-3.4v-4.9C26 8.9 21.2 3 16 3Z"
        fill="currentColor"
      />
      <path
        d="M17.5 10h5.3c1.4 0 2.5 1.1 2.5 2.5v3.1c0 .4-.3.7-.7.7h-7.7c-.5 0-.8-.4-.8-.8v-4.6c0-.5.6-.9 1.4-.9Z"
        fill={visor}
      />
    </svg>
  );
}

const flagCls = "w-5 h-[15px] block rounded-[2px] shadow-[inset_0_0_0_1px_rgba(0,0,0,0.15)]";
const FlagES = () => (
  <svg viewBox="0 0 20 14" className={flagCls}><rect width="20" height="14" fill="#AA151B" /><rect y="3.5" width="20" height="7" fill="#F1BF00" /></svg>
);
const FlagGB = () => (
  <svg viewBox="0 0 20 14" className={flagCls}>
    <rect width="20" height="14" fill="#012169" />
    <path d="M0 0L20 14M20 0L0 14" stroke="#fff" strokeWidth="2.6" />
    <path d="M0 0L20 14M20 0L0 14" stroke="#C8102E" strokeWidth="1.1" />
    <path d="M10 0V14M0 7H20" stroke="#fff" strokeWidth="4.4" />
    <path d="M10 0V14M0 7H20" stroke="#C8102E" strokeWidth="2.5" />
  </svg>
);
const FlagDE = () => (
  <svg viewBox="0 0 20 14" className={flagCls}><rect width="20" height="14" fill="#FFCE00" /><rect width="20" height="9.4" fill="#DD0000" /><rect width="20" height="4.7" fill="#000" /></svg>
);
const FlagIT = () => (
  <svg viewBox="0 0 20 14" className={flagCls}><rect width="20" height="14" fill="#fff" /><rect width="6.7" height="14" fill="#009246" /><rect x="13.3" width="6.7" height="14" fill="#CE2B37" /></svg>
);

export const LANGS = [
  { id: "es", label: "Español", Flag: FlagES },
  { id: "en", label: "English", Flag: FlagGB },
  { id: "de", label: "Deutsch", Flag: FlagDE },
  { id: "it", label: "Italiano", Flag: FlagIT },
];

export default function Header({ onBook }) {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    ["#fleet", t.nav.fleet],
    ["#experience", t.nav.experience],
    ["#routes", t.nav.routes],
    ["#reviews", t.nav.reviews],
    ["#faq", t.nav.faq],
    ["#contact", t.nav.contact],
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-white/95 backdrop-blur-md border-b-2 border-topacio-ink">
      <div className={`max-w-7xl mx-auto px-6 flex items-center justify-between transition-all duration-300 ${scrolled ? "h-16" : "h-20"}`}>
        <a href="#top" data-testid="header-logo" className="flex items-center gap-3 group">
          <img src="/logo-topacio.jpg" alt="Rent a Moto Topacio"
            className={`w-auto rounded-md border-2 border-topacio-ink shadow-[3px_3px_0_0_#111] transition-all duration-300 ${scrolled ? "h-9" : "h-12"}`} />
          <span className="hidden sm:flex w-9 h-9 items-center justify-center bg-white border-2 border-topacio-ink rounded-lg shadow-[3px_3px_0_0_#111] -rotate-6 group-hover:rotate-6 group-hover:-translate-y-0.5 transition-transform duration-300">
            <HelmetIcon className="w-5 h-5 text-topacio-ink" />
          </span>
          <span className="hidden xl:inline-flex badge-brutal bg-topacio-red text-white shadow-[2px_2px_0_0_#111] -rotate-1">
            Desde 1991
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map(([href, label]) => (
            <a key={href} href={href} data-testid={`nav-${href.slice(1)}`}
              className="group relative text-sm font-semibold text-topacio-faded hover:text-topacio-ink transition-colors pt-1">
              {label}
              <span className="absolute left-0 -bottom-1.5 h-[3px] w-0 bg-brand group-hover:w-full transition-[width] duration-300" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div className="flex items-center border-2 border-topacio-ink rounded-lg overflow-hidden bg-white shadow-[2px_2px_0_0_#111]">
            {LANGS.map(({ id, label, Flag }) => (
              <button key={id} data-testid={`lang-${id}`} title={label} aria-label={label} onClick={() => setLang(id)}
                className={`px-2.5 py-2 flex items-center transition-all duration-200 ${lang === id ? "bg-brand" : "hover:bg-topacio-sand opacity-60 hover:opacity-100"}`}>
                <Flag />
              </button>
            ))}
          </div>
          <button data-testid="header-book-btn" onClick={() => onBook(null)}
            className="hidden sm:inline-flex btn-primary !px-5 !py-2.5 !text-xs group">
            <HelmetIcon className="w-4 h-4 text-topacio-ink transition-transform duration-300 group-hover:-rotate-12 group-hover:scale-110" visor="#ffffff" />
            {t.nav.book}
          </button>
          <button data-testid="mobile-menu-btn" className="lg:hidden text-topacio-ink" onClick={() => setOpen(!open)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="lg:hidden border-t-2 border-topacio-ink bg-white">
          {links.map(([href, label]) => (
            <a key={href} href={href} data-testid={`mobile-nav-${href.slice(1)}`} onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-6 py-3 text-sm font-semibold text-topacio-faded border-b border-zinc-100 hover:text-topacio-ink">
              {label}
            </a>
          ))}
        </nav>
      )}
    </header>
  );
}
